import { useState, useEffect, useRef } from 'react';

const HEALTH_URL          = '/api/v1/module/health';
const PING_URL            = '/api/v1/module/ping';
const HEALTH_TIMEOUT_MS   = 4_000;
const INTERVAL_ONLINE_MS  = 30_000;
const INTERVAL_OFFLINE_MS = 5_000;

/**
 * Silent online/offline detection — never shows an intermediate "Checking…"
 * state that would cause the UI to blink on every mount or probe cycle.
 *
 * Rules:
 *  - Initial state is the best available guess (navigator.onLine / window.pms).
 *    The component renders immediately in a stable state — no "Checking…" flash.
 *  - Online → Offline requires FAIL_STREAK_THRESHOLD consecutive probe failures
 *    so a single dropped packet or brief server restart does not blink the UI.
 *  - Offline → Online flips immediately on the first successful probe (fast recovery).
 *  - In Electron (window.pms present) the SyncWorker pushes authoritative
 *    connectivity via onSyncStatus — we do NOT run our own HTTP probes at all.
 *
 * The returned `checking` is kept for backward compat but is always `false`
 * after the first render so callers that gate on it don't block.
 */

const FAIL_STREAK_THRESHOLD = 2; // consecutive failures needed to declare offline

export function useOnlineStatus() {
  // Best-guess initial state — never starts as "unknown"
  const [online, setOnline] = useState(() => {
    if (typeof window !== 'undefined' && window.pms) return true;
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  });

  // Always false — we never surface a "Checking…" indicator.
  // Kept only so existing destructuring `{ online, checking }` doesn't break.
  const [checking] = useState(false);

  const onlineRef    = useRef(online);
  const failStreak   = useRef(0);        // consecutive probe failures
  const intervalRef  = useRef(null);

  // Update both state and ref atomically
  const setStatus = (reachable) => {
    const was = onlineRef.current;
    onlineRef.current = reachable;
    setOnline(reachable);
    if (reachable && !was)  window.dispatchEvent(new Event('hotelmis:backend:online'));
    if (!reachable && was)  window.dispatchEvent(new Event('hotelmis:backend:offline'));
  };

  const scheduleNext = (delayMs) => {
    clearTimeout(intervalRef.current);
    intervalRef.current = setTimeout(async () => {
      await probe();
      scheduleNext(onlineRef.current ? INTERVAL_ONLINE_MS : INTERVAL_OFFLINE_MS);
    }, delayMs);
  };

  const probe = async () => {
    // OS says offline — trust it immediately
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      failStreak.current = FAIL_STREAK_THRESHOLD;
      setStatus(false);
      return;
    }

    const ctrl = new AbortController();
    const tid  = setTimeout(() => ctrl.abort(), HEALTH_TIMEOUT_MS);
    try {
      let res = await fetch(HEALTH_URL, { method: 'GET', cache: 'no-store', signal: ctrl.signal });
      if (res.status === 404) {
        res = await fetch(PING_URL, { method: 'GET', cache: 'no-store', signal: ctrl.signal });
      }
      clearTimeout(tid);

      if (res.ok) {
        failStreak.current = 0;
        setStatus(true);           // Offline → Online: flip immediately
      } else {
        failStreak.current += 1;
        if (failStreak.current >= FAIL_STREAK_THRESHOLD) setStatus(false);
        // else: still counting — don't change the displayed state yet
      }
    } catch {
      clearTimeout(tid);
      failStreak.current += 1;
      if (failStreak.current >= FAIL_STREAK_THRESHOLD) setStatus(false);
    }
  };

  useEffect(() => {
    // ── Desktop (Electron) path ────────────────────────────────────────────
    // The SyncWorker's ConnectivityMonitor probes the server every 15 s and
    // pushes the result.  We subscribe to that push — no duplicate HTTP probes.
    if (window.pms?.onSyncStatus) {
      // Seed from the main process immediately — getConnectionStatus() is a
      // cheap synchronous IPC call that returns the last-known connection state.
      // This avoids the optimistic-online gap that caused the status to blink.
      const seedStatus = async () => {
        try {
          // Prefer getConnectionStatus (instant, no probe)
          const state = await window.pms.getConnectionStatus?.();
          if (state === 'ONLINE' || state === 'OFFLINE') {
            setStatus(state === 'ONLINE');
            return;
          }
        } catch (_) {}
        // Fallback: extract from getSyncStatus
        try {
          const r = await window.pms.getSyncStatus?.();
          if (!r) return;
          const conn = r.connection ?? r.data?.connection;
          if (conn === 'ONLINE' || conn === 'OFFLINE') setStatus(conn === 'ONLINE');
        } catch (_) {}
      };
      seedStatus();

      const unsub = window.pms.onSyncStatus((s) => {
        const reachable = s.connection === 'ONLINE';
        failStreak.current = reachable ? 0 : FAIL_STREAK_THRESHOLD;
        setStatus(reachable);
      });
      return unsub;
    }

    // ── Browser path ───────────────────────────────────────────────────────
    // Run probe once now to confirm the best-guess initial state, then poll.
    // We don't await it — the UI stays in its initial state until the result
    // comes back, with no visible flash.
    probe();
    scheduleNext(INTERVAL_ONLINE_MS);

    const onBrowserOnline = () => {
      failStreak.current = 0;
      probe();
      scheduleNext(INTERVAL_ONLINE_MS);
    };
    const onBrowserOffline = () => {
      failStreak.current = FAIL_STREAK_THRESHOLD;
      setStatus(false);
      scheduleNext(INTERVAL_OFFLINE_MS);
    };

    window.addEventListener('online',  onBrowserOnline);
    window.addEventListener('offline', onBrowserOffline);
    return () => {
      clearTimeout(intervalRef.current);
      window.removeEventListener('online',  onBrowserOnline);
      window.removeEventListener('offline', onBrowserOffline);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { online, checking };
}